<template>
  <form
    @submit.prevent="handleSubmit"
    class="mt-4 rounded-app bg-white p-4 shadow-md md:p-8"
  >
    <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
      <InputText
        id="first_name"
        v-model:value="pageFields.first_name"
        v-model:errors="errors.first_name"
        :label="__('First Name')"
        :placeholder="__('First Name')"
        required
      />

      <InputText
        id="last_name"
        v-model:value="pageFields.last_name"
        v-model:errors="errors.last_name"
        :label="__('Last Name')"
        :placeholder="__('Last Name')"
        required
      />

      <InputEmail
        id="email"
        v-model:value="pageFields.email"
        v-model:errors="errors.email"
        :label="__('Email')"
        :placeholder="__('Email')"
        required
      >
        <template #icon>
          <Icon class="text-lg text-neutral-400" name="mail" is-filled />
        </template>
      </InputEmail>
      <div class="grid grid-cols-1 gap-x-2 gap-y-4 sm:grid-cols-5">
        <InputSelect
          id="country_prefix"
          v-model:value="pageFields.country_prefix"
          v-model:errors="errors.country_prefix"
          :label="__('Country prefix')"
          :placeholder="__('Country prefix')"
          :options="options.country_prefix"
          class="col-span-1 sm:col-span-2"
          required
        />

        <InputText
          id="phone_number"
          v-model:value="pageFields.phone"
          v-model:errors="errors.phone"
          class="col-span-1 sm:col-span-3"
          :label="__('Phone Number')"
          :placeholder="__('754 782 082')"
          required
        >
          <template #icon>
            <Icon class="text-lg text-neutral-400" name="phone" is-filled />
          </template>
        </InputText>
      </div>

      <InputLocation
        v-model:value="pageFields.current_location"
        v-model:errors="errors.current_location"
        :label-country="__('Country of residence')"
        :label-city="__('City of residence')"
        :placeholder-country="__('Add country')"
        :placeholder-city="__('Add city')"
        :options="options.country"
        required
        class="md:col-span-2"
      />
      <div>
        <InputPassword
          id="password"
          v-model:value="pageFields.password"
          v-model:errors="errors.password"
          :label="__('Password')"
          :placeholder="__('Choose your password')"
          required
        />
        <div v-if="startTyping">
          <div class="flex flex-row gap-1 py-3 md:gap-2">
            <div class="flex items-center gap-0 pt-3 md:gap-1 md:pt-0">
              <Icon
                :class="[
                  passwordRules.min === null
                    ? 'text-gray-400'
                    : passwordRules.min
                      ? 'text-green-600'
                      : 'text-red-600'
                ]"
                :name="
                  passwordRules.min === null || passwordRules.min
                    ? 'done'
                    : 'close'
                "
              />
              <span class="text-xs text-zinc-600">
                {{ __('min 8 characters') }}
              </span>
            </div>
            <div class="flex items-center gap-0 md:gap-1">
              <Icon
                :class="[
                  passwordRules.min === null
                    ? 'text-gray-400'
                    : passwordRules.upper
                      ? 'text-green-600'
                      : 'text-red-600'
                ]"
                :name="
                  passwordRules.min === null || passwordRules.upper
                    ? 'done'
                    : 'close'
                "
              />
              <span class="text-xs text-zinc-600">
                {{ __('one uppercase letter') }}
              </span>
            </div>
            <div class="flex items-center gap-0 md:gap-1">
              <Icon
                :class="[
                  passwordRules.min === null
                    ? 'text-gray-400'
                    : passwordRules.lower
                      ? 'text-green-600'
                      : 'text-red-600'
                ]"
                :name="
                  passwordRules.min === null || passwordRules.lower
                    ? 'done'
                    : 'close'
                "
              />
              <span class="text-xs text-zinc-600">
                {{ __('one lowercase letter') }}
              </span>
            </div>
          </div>
          <div class="flex flex-row gap-1 pb-3 md:gap-2">
            <div class="flex items-center gap-0 md:gap-1">
              <Icon
                :class="[
                  passwordRules.min === null
                    ? 'text-gray-400'
                    : passwordRules.number
                      ? 'text-green-600'
                      : 'text-red-600'
                ]"
                :name="
                  passwordRules.min === null || passwordRules.number
                    ? 'done'
                    : 'close'
                "
              />
              <span class="text-xs text-zinc-600">
                {{ __('one number') }}
              </span>
            </div>
            <div class="flex items-center gap-0 md:gap-1">
              <Icon
                :class="[
                  passwordRules.min === null
                    ? 'text-gray-400'
                    : passwordRules.special
                      ? 'text-green-600'
                      : 'text-red-600'
                ]"
                :name="
                  passwordRules.min === null || passwordRules.special
                    ? 'done'
                    : 'close'
                "
              />
              <span class="text-xs text-zinc-600">
                {{ __('one special character (@, #, $)') }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <InputPassword
        id="password_confirmation"
        v-model:value="pageFields.password_confirmation"
        v-model:errors="errors.password_confirmation"
        :label="__('Confirm your password')"
        :placeholder="__('Rewrite your password')"
        required
        class="pb-8 md:col-start-2"
      />
    </div>
    <div>
      <div class="mb-11 w-full">
        <UploadCv
          v-model:value="pageFields.file"
          :errors="errors.file || []"
          @update:value="handleCVChange"
        />
      </div>
      <div class="mb-11 space-y-2">
        <InputCheckbox
          id="recruit"
          v-model:value="pageFields.recruit"
          v-model:errors="errors.recruit"
          :label="__('I agree to the')"
          class="mb-6"
        >
          <template #default>
            <a href="/termeni-si-conditii" class="text-black underline">{{
              __('terms and conditions')
            }}</a>
            {{ __(' and the ') }}
            <a href="/termeni-si-conditii" class="text-black underline">{{
              __('privacy policy')
            }}</a
            >.
          </template>
        </InputCheckbox>

        <div class="flex items-start gap-2">
          <InputCheckbox
            id="marketing"
            v-model:value="pageFields.marketing"
            v-model:errors="errors.marketing"
            :label="
              __(
                'I would like to receive updates from Jobful about career opportunities that match my skills.'
              )
            "
          />
        </div>
      </div>

      <div class="text-center">
        <p class="text-sm">
          {{ __('Already have an account?') }}
          <a
            @click="redirectToLogin"
            class="cursor-pointer font-medium text-primary"
            >{{ __('Sign In') }}</a
          >
        </p>
      </div>
    </div>

    <div
      class="mt-4 flex flex-col items-center gap-2 md:flex-row-reverse md:justify-center"
    >
      <Button
        class="flex items-center justify-center gap-1.5"
        :type="isLoading ? 'button' : 'submit'"
        :disabled="isLoading"
        style="width: 10rem !important"
        :content="__('Submit Form')"
      >
        <Icon
          v-if="isLoading"
          class="animate-spin text-lg"
          name="progress_activity"
        />
        <span>{{ __('Sign Up') }}</span>
      </Button>
      <Button
        :content="__('Cancel')"
        @click="() => emit('close')"
        :disabled="isLoading"
      />
    </div>
    <div class="mx-auto flex w-[17rem] flex-col">
      <SocialLoginButton></SocialLoginButton>
    </div>
  </form>
</template>

<script setup>
import {nextTick, ref, watch} from 'vue';
  import axios from 'axios';
  import { Button } from '../../../../components/global';
  import { __ } from '../../../../helpers';
  import { useToast } from 'vue-toastification';
  import { InputLocation } from '../../../../components/form/input-location';
  import { InputText } from '../../../../components/form/input-text';
  import { InputEmail } from '../../../../components/form/input-email';
  import { Icon } from '../../../../components/block/icon';
  import { InputSelect } from '../../../../components/form/input-select';
  import { UploadCv } from './upload-cv';
  import { InputPassword } from '../../../../components/form/input-password';
  import SocialLoginButton from '../../auth/components/SocialLoginButton.vue';
  import { InputCheckbox } from '../../../../components/form/input-checkbox';
  import { useForm } from "@/utils";

  const props = defineProps({
    job: {
      type: Object,
      required: true
    },
    options: {
      type: Object,
      required: true
    }
  });
console.log('props are');
console.log(props);
  const toast = useToast();
  const emit = defineEmits(['apply-success']);
  const startTyping = ref(false);

  const pageFields = ref({
    first_name: '',
    last_name: '',
    email: '',
    country_prefix: null,
    phone: '',
    current_location: null,
    file: null,
    recruit: false,
    marketing: false,
    password: '',
    password_confirmation: ''
  });

  const passwordRules = ref({
    min: null,
    upper: null,
    lower: null,
    number: null,
    special: null
  });

  const redirectToLogin = () => {
    window.location.href = route('login.user');
  };

  watch(
    [
      () => pageFields.value.password,
      () => pageFields.value.password_confirmation
    ],
    ([newPassword, confirmPassword]) => {
      if (newPassword.length > 0) {
        startTyping.value = true;
      }

      if (newPassword.length === 0) {
        startTyping.value = false;
      }

      passwordRules.value.min =
        newPassword.length === 0 ? null : newPassword.length >= 8;
      passwordRules.value.upper =
        newPassword.length === 0 ? null : /[A-Z]/.test(newPassword);
      passwordRules.value.lower =
        newPassword.length === 0 ? null : /[a-z]/.test(newPassword);
      passwordRules.value.number =
        newPassword.length === 0 ? null : /\d/.test(newPassword);
      passwordRules.value.special =
        newPassword.length === 0 ? null : /[^a-zA-Z0-9]/.test(newPassword);

      if (confirmPassword && newPassword !== confirmPassword) {
        errors.value.password_confirmation = [
          __('Your new password must match the confirmation password')
        ];
      } else {
        errors.value.password_confirmation = [];
      }
    }
  );

  const handleCVChange = (file) => {
    pageFields.value.file = file;
  };

  const {formData, isLoading, updateFormData, resetForm, submitForm, errors} =
      useForm(pageFields, {
        method: "post",
        url: route('redesign.jobs.apply_no_account', {
          company: props.job.company.slug,
          job: props.job.slug
        }),
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });

  const handleSubmit = async () => {
    try {
      const submitResponse = await submitForm();

      if (Object.keys(errors.value).length === 0) {
        toast.success(submitResponse.message);
        emit('close');
      } else {
        toast.error(__("You have some errors in your form!"));
      }
    } catch (error) {
      toast.error(error.response.data.message || __("You have some errors in your form!"));
    }
  };

</script>
